import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "tco_cart_v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = (product, { quantity = 1, flavor, selections } = {}) => {
    setItems((prev) => {
      const key = `${product.id}|${flavor || ""}|${(selections || []).join(",")}`;
      const existing = prev.find((p) => p._key === key);
      if (existing) {
        return prev.map((p) => (p._key === key ? { ...p, quantity: p.quantity + quantity } : p));
      }
      return [
        ...prev,
        {
          _key: key,
          id: product.id,
          slug: product.slug,
          name: product.name,
          price: product.price,
          image_url: product.image_url,
          category: product.category,
          flavor: flavor || product.flavor || null,
          selections: selections || [],
          quantity,
        },
      ];
    });
    setDrawerOpen(true);
  };

  const addReferralReward = (product, rewardId) => {
    setItems((prev) => [
      ...prev.filter((item) => !item.is_referral_reward),
      {
        _key: `referral-reward:${rewardId}`,
        id: product.id,
        slug: product.slug,
        name: `${product.name} — Referral reward`,
        price: 0,
        image_url: product.image_url,
        category: product.category,
        flavor: product.flavor || null,
        selections: [],
        quantity: 1,
        is_referral_reward: true,
        referral_reward_id: rewardId,
      },
    ]);
    setDrawerOpen(true);
  };

  const updateQty = (key, qty) =>
    setItems((prev) => prev.map((p) => (p._key === key ? { ...p, quantity: Math.max(1, qty) } : p)));

  const removeItem = (key) => setItems((prev) => prev.filter((p) => p._key !== key));
  const clear = () => setItems([]);

  const subtotal = useMemo(
    () => items.reduce((s, it) => s + it.price * it.quantity, 0),
    [items]
  );
  const count = items.reduce((s, i) => s + i.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        addReferralReward,
        updateQty,
        removeItem,
        clear,
        subtotal,
        count,
        drawerOpen,
        setDrawerOpen,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
